import { USE_BACKEND_API, API_BASE_URL } from '../config/apiConfig';
import { supabase } from '../services/supabaseService';

/**
 * Normalizes image URLs from various formats to full public URLs
 * Handles:
 * - Full URLs (already public URLs) - returns as-is
 * - Storage paths (like "tours/image.jpg") - converts to public URL
 * - Empty/null/undefined - returns placeholder
 */
export const normalizeImageUrl = (imagePath: string | null | undefined): string => {
  // Return placeholder if no image
  if (!imagePath || imagePath.trim() === '') {
    return 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
  }

  // If it's already a full URL (http/https), check if it's localhost in production
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // If we're using backend API and the URL is localhost but we're NOT on localhost
    const isLocalhostUrl = imagePath.includes('localhost') || imagePath.includes('127.0.0.1');
    const isActualLocalhost = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (USE_BACKEND_API && isLocalhostUrl && !isActualLocalhost) {
      // Replace the localhost part with the production base URL (strip /api if present)
      const prodBaseUrl = API_BASE_URL.replace(/\/api$/, '');
      const pathPart = imagePath.split(/:\d+\//)[1] || imagePath.split(/localhost\//)[1];
      if (pathPart) {
        return `${prodBaseUrl}/${pathPart}`;
      }
    }

    // Optimization: Add Cloudinary auto-format and quality if it's a Cloudinary URL
    if (imagePath.includes('res.cloudinary.com')) {
      // Check if it already has transformations (contains /upload/ followed by something other than v123...)
      if (imagePath.includes('/upload/') && !imagePath.includes('/upload/f_auto,q_auto')) {
        return imagePath.replace('/upload/', '/upload/f_auto,q_auto,w_800,c_limit/');
      }
    }
    return imagePath;
  }

  // If it's a data URL or blob URL, return as-is
  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    return imagePath;
  }

  // Fallback to Supabase for storage paths
  try {
    const { data } = supabase.storage
      .from('images')
      .getPublicUrl(imagePath);

    return data.publicUrl;
  } catch (error) {
    console.warn('Failed to get public URL for image path:', imagePath, error);
    // Fallback to placeholder
    return 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
  }
};

/**
 * Normalizes an array of image URLs
 */
export const normalizeImageUrls = (images: (string | null | undefined)[] | null | undefined): string[] => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return [];
  }

  return images
    .map(img => normalizeImageUrl(img))
    .filter(url => url && url.trim() !== ''); // Remove empty strings
};



